import { isRight } from "fp-ts/Either";
import i18n from "i18next";
import url from "url-parse";

import { CalendarPayloadResolver } from "@ihr-radioedit/inferno-core";
import { removePrefix, slugify } from "@ihr-radioedit/inferno-core";

import { TagContext, Tagger, Tags } from "../../../lib/tagging-type";
import { isCalendarPublishRecord, isFeedResult, isItemBlock } from "../../../lib/guards";
import { sanitize } from "../../../utilities/sanitize";
import { generateMetaTag, setGeneralMetaTags } from "../../util";
import { buildCalendarTitles } from "../../utils/calendar-title";

export const ampCalendarArticleTemplateTags: Tagger = (state: Tags, context: TagContext) => {
  const { site, block, env } = context;
  if (
    isItemBlock(block) &&
    block.item &&
    isFeedResult(block.item.result) &&
    isCalendarPublishRecord(block.item.result.record)
  ) {
    const content = block.item?.result.record;
    const resolvedPayload = CalendarPayloadResolver.decode(content.payload);
    const payload = isRight(resolvedPayload) ? resolvedPayload.right : null;

    const keywords: string[] = [];
    payload?.fields.keywords?.value?.forEach(value => {
      if (value) {
        keywords.push(removePrefix(/^\w+\//)(value));
      }
    });

    let itemUrl = payload?.canonical_url || "";
    if (itemUrl && !url(itemUrl).protocol) {
      itemUrl = `https:${itemUrl}`;
    }

    const stationName = site.sections.general?.name || "";
    const venue = payload?.fields.venue_name?.value || "";
    const address = payload?.fields.venue_address?.value || "";
    const seoTitle = payload?.fields.social_title?.value || block.value.title || "";
    const timezone = site.index?.timeZone ? site.index.timeZone : env.DEFAULT_TIMEZONE;

    const { title, socialTitle } = buildCalendarTitles({
      payload,
      contentTitle: content.summary.title || "",
      blockTitle: block.value.title || "",
      stationName,
      timezone,
    });

    if (seoTitle) {
      state.surrogateKeys?.add(`event/${slugify(seoTitle)}`);
    }

    if (venue) {
      keywords.push(...sanitize(venue.trim().toLowerCase()).split(" "));
    }

    if (address) {
      keywords.push(...sanitize(address.trim().toLowerCase().replace(/,/g, "")).split(" "));
    }

    let description = content.summary.description || "";
    if (!description) {
      const station = `${site.sections.general?.name} - ${site.sections.general?.positioner}`;
      description = i18n.t("event_description", { station, stationName });
    }

    let { metadata } = state;
    metadata = setGeneralMetaTags(metadata, {
      titles: {
        default: title,
        social: socialTitle,
      },
      description,
      keywords,
    });

    metadata.set(...generateMetaTag("name", "robots", "max-image-preview:large"));

    return {
      ...state,
      metadata,
    };
  }

  return state;
};
